import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
	PEOPLE_SEARCH_CATEGORIES,
	PeopleSearchCategory,
	isCategoryActive,
} from "@/types/peopleSearch";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";

export function FilterRail() {
	const { t } = useTranslation();
	const {
		draft,
		activeCategory,
		setActiveCategory,
		railFilterText,
		setRailFilterText,
		dirtyCategories,
		reset,
	} = usePeopleSearch();

	const categoryLabel = (category: PeopleSearchCategory) =>
		t(`peopleSearch.categories.${category}`, category);

	const visibleCategories = useMemo(() => {
		const query = railFilterText.trim().toLowerCase();
		if (query === '')
			return PEOPLE_SEARCH_CATEGORIES;
		return PEOPLE_SEARCH_CATEGORIES.filter(category =>
			categoryLabel(category).toLowerCase().includes(query));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [railFilterText, t]);

	return (
		<div className="flex flex-col h-full">
			<div className="relative mb-2">
				<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					value={railFilterText}
					onChange={e => setRailFilterText(e.target.value)}
					placeholder={t("peopleSearch.searchFilters", "Search filters…")}
					className="pl-8"
				/>
			</div>
			<nav
				className="flex-1 overflow-y-auto space-y-0.5"
				aria-label={t("peopleSearch.rail.navLabel", "Filter categories")}
			>
				{visibleCategories.map(category => {
					const active = isCategoryActive(draft, category);
					const dirty = dirtyCategories.includes(category);
					return (
						<button
							key={category}
							type="button"
							onClick={() => setActiveCategory(category)}
							aria-current={activeCategory === category || undefined}
							className={cn(
								"w-full flex items-center justify-between px-3 py-2 rounded-sm text-sm text-left transition-colors",
								activeCategory === category
									? "bg-muted font-medium"
									: "hover:bg-muted/50"
							)}
						>
							<span>{categoryLabel(category)}</span>
							{(active || dirty) && (
								<span
									className={cn(
										"h-2 w-2 rounded-full",
										dirty ? "bg-amber-500" : "bg-digger-blue"
									)}
									aria-label={dirty
										? t("peopleSearch.rail.unapplied", "Unapplied changes")
										: t("peopleSearch.rail.active", "Active filters")}
								/>
							)}
						</button>
					);
				})}
			</nav>
			<Button variant="ghost" size="sm" onClick={reset} className="mt-2 justify-start text-muted-foreground">
				<RotateCcw className="h-4 w-4 mr-2" />
				{t("peopleSearch.reset", "Reset")}
			</Button>
		</div>
	);
}
